import { byId, setText } from "./domUtils";
import type { Root } from "./prototypeTypes";
import { getRuntime } from "./runtimeAccessor";
import { backFromContextualView, showContextualView } from "./navigationController";
import type { AiJobDetailResponse } from "./apiClientResponses";
import type { PrototypeRuntime } from "./prototypeTypes";
import { safeExternalHttpsUrl } from '../../domain/externalUrlPolicy';
import { aiChapterDisplayLabel } from '../../domain/aiChapterTargetPolicy';
import { captureAiHistoryReturnState, requestAiHistoryReturn } from './aiCreateHistoryReturnState';

export interface AiJobDetail {
	id: string;
	title: string;
	subtitle: string | null;
	description: string | null;
	prompt: string;
	requestedChapters: string;
	requestedChaptersRange?: string | null;
	status: string;
	queuedAt: string;
	completedAt: string | null;
	strengthenItems: string[];
	weakenItems: string[];
	links: Array<{ url: string; label: string }>;
	attachments: Array<{ name: string; size?: number; mimeType?: string }>;
	topicSlug: string | null;
	chapterCount: number | null;
}

export const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
	queued: { text: "In Progress", cls: "pill-progress" },
	processing: { text: "In Progress", cls: "pill-progress" },
	running: { text: "In Progress", cls: "pill-progress" },
	completed: { text: "Completed", cls: "pill-completed" },
	failed: { text: "Failed", cls: "pill-rejected" },
};

/**
 * Show the AI job detail view. Paints a skeleton immediately, fetches
 * the job data, then renders the full detail or a not-found state.
 * Mirrors the showIdeaDetail pattern.
 */
export function showAiJobDetail(jobId: string, root: Root): boolean {
	captureAiHistoryReturnState(root);
	paintSkeleton(root);
	const rt = getRuntime(root);
	if (rt) rt.__activeAiJobId = jobId;
	if (rt?.api?.getAiJobDetail) {
		void rt.api.getAiJobDetail(jobId).then((res) => {
			const job = res?.job ?? null;
			if (!job) {
				paintNotFound(root);
				return;
			}
			paintDetail(root, job);
		}).catch(() => paintNotFound(root));
	} else {
		paintNotFound(root);
	}
	showContextualView("ai-job-detail", root);
	return true;
}

/** Return from a job detail without discarding the originating History intent. */
export function backFromAiJobDetail(root: Root): boolean {
	requestAiHistoryReturn(root);
	backFromContextualView(root, { view: 'ai-create', tab: 'ai-create' });
	return true;
}

/**
 * Re-hydration hook for the showView "ai-job-detail" branch.
 * If the controller already started fetching (via showAiJobDetail),
 * this is a no-op. It exists so a future deeplink router can hand a
 * jobId into the view.
 */
export function hydrateAiJobDetail(root: Root, api: PrototypeRuntime["api"], jobId: string): void {
	// If the title is still "Loading…" the initial fetch hasn't resolved yet.
	const titleEl = byId(root, "aiJobDetailTitle");
	if (titleEl && titleEl.textContent !== "Loading…") return;
	void api.getAiJobDetail(jobId).then((res) => {
		const job = res?.job ?? null;
		if (!job) {
			paintNotFound(root);
			return;
		}
		paintDetail(root, job);
	}).catch(() => paintNotFound(root));
}

function paintSkeleton(root: Root) {
	setText(root, "aiJobDetailTitle", "Loading…");
	setText(root, "aiJobDetailSubtitle", "");
	setText(root, "aiJobDetailTitleValue", "—");
	setText(root, "aiJobDetailSubtitleValue", "—");
	setText(root, "aiJobDetailDesc", "Fetching job details…");
	setText(root, "aiJobDetailChapters", "—");
	setText(root, "aiJobDetailStatus", "-");
	setText(root, "aiJobDetailTime", "");
	const linksSection = byId(root, "aiJobDetailLinksSection");
	if (linksSection) {
		const linksEl = byId(root, "aiJobDetailLinks");
		if (linksEl) linksEl.innerHTML = '<span style="font-size:14px;color:var(--fg-tertiary);">Loading…</span>';
	}
	const attachSection = byId(root, "aiJobDetailAttachSection");
	if (attachSection) attachSection.hidden = true;
	const prefsSection = byId(root, "aiJobDetailPrefsSection");
	if (prefsSection) prefsSection.hidden = true;
	const topicSection = byId(root, "aiJobDetailTopicSection");
	if (topicSection) topicSection.hidden = true;
}

function paintNotFound(root: Root) {
	setText(root, "aiJobDetailTitle", "Job not found");
	setText(root, "aiJobDetailSubtitle", "This AI Create job may have been removed, or the link is wrong.");
	setText(root, "aiJobDetailStatus", "Not Found");
	setText(root, "aiJobDetailTime", "");
	// Hide all sections except the header
	const sections = root.querySelectorAll<HTMLElement>(".ai-job-detail-section");
	sections.forEach((s) => (s.hidden = true));
	const topicSection = byId(root, "aiJobDetailTopicSection");
	if (topicSection) topicSection.hidden = true;
}

function paintDetail(root: Root, job: AiJobDetail) {
	// Show all sections (in case paintNotFound hid them)
	root.querySelectorAll<HTMLElement>(".ai-job-detail-section").forEach((s) => (s.hidden = false));

	// Header
	setText(root, "aiJobDetailTitle", job.title);
	setText(root, "aiJobDetailSubtitle", job.subtitle ?? "");

	// Status badge
	const statusEl = byId(root, "aiJobDetailStatus");
	if (statusEl) {
		const badge = STATUS_LABEL[job.status] ?? { text: "In Progress", cls: "pill-progress" };
		statusEl.textContent = badge.text;
		statusEl.className = `pill ${badge.cls}`;
	}
	setText(root, "aiJobDetailTime", formatTime(job.queuedAt, job.completedAt));

	// Section 1: Topic Basics
	setText(root, "aiJobDetailTitleValue", job.title);
	setText(root, "aiJobDetailSubtitleValue", job.subtitle || "—");

	// Section 2: Reference Material - Links
	const linksEl = byId(root, "aiJobDetailLinks");
	if (linksEl) {
		if (job.links.length > 0) {
			linksEl.innerHTML = renderAiJobDetailReferenceLinks(job.links);
		} else {
			linksEl.innerHTML =
				'<span class="ai-job-detail-empty-row"><svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg><span>No reference links provided.</span></span>';
		}
	}

	// Section 2: Attachments
	const attachSection = byId(root, "aiJobDetailAttachSection");
	const attachEl = byId(root, "aiJobDetailAttach");
	if (attachSection && attachEl) {
		if (job.attachments.length > 0) {
			attachSection.hidden = false;
			attachEl.innerHTML = job.attachments
				.map((a) => `<div class="ai-job-detail-attachment"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>${escapeHtml(a.name)}${a.size ? ` <span class="ai-job-detail-attachment-size">(${formatSize(a.size)})</span>` : ""}</div>`)
				.join("");
		} else {
			attachSection.hidden = true;
		}
	}

	// Section 3: Learning Preferences
	setText(root, "aiJobDetailDesc", job.description ?? job.prompt ?? "—");
	setText(root, "aiJobDetailChapters", aiChapterDisplayLabel(job.requestedChaptersRange ?? job.requestedChapters) || "AI decides");

	// AI Preferences (strengths / weaknesses)
	const prefsSection = byId(root, "aiJobDetailPrefsSection");
	if (prefsSection) {
		const hasStrengths = job.strengthenItems.length > 0;
		const hasWeaknesses = job.weakenItems.length > 0;
		prefsSection.hidden = !hasStrengths && !hasWeaknesses;
		if (hasStrengths) {
			const el = byId(root, "aiJobDetailStrengths");
			if (el) el.innerHTML = renderPrefList("Expected Strengths", job.strengthenItems);
		}
		if (hasWeaknesses) {
			const el = byId(root, "aiJobDetailWeaknesses");
			if (el) el.innerHTML = renderPrefList("What to Spend Less Time On", job.weakenItems);
		}
	}

	// Topic link (if completed)
	const topicSection = byId(root, "aiJobDetailTopicSection");
	if (topicSection) {
		if (job.topicSlug) {
			topicSection.hidden = false;
			const btn = byId(root, "aiJobDetailViewTopic");
			if (btn) btn.setAttribute("data-proto-click", `showTopicDetail('${escapeAttr(job.topicSlug)}')`);
		} else {
			topicSection.hidden = true;
		}
	}
}

/** Render untrusted AI job references as safe external links or inert fallback text. */
export function renderAiJobDetailReferenceLinks(links: Array<{ url: string; label: string }>): string {
	return links
		.map(({ url, label }) => {
			const href = safeExternalHttpsUrl(url);
			if (href) {
				return `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer" class="ai-job-detail-link">${escapeHtml(url.trim())}</a>`;
			}
			return `<span class="ai-job-detail-link" aria-disabled="true">${escapeHtml(label || url)}</span>`;
		})
		.join("");
}

function renderPrefList(label: string, items: string[]): string {
	return `<span class="ai-job-detail-field-label">${escapeHtml(label)}</span><div class="ai-job-detail-pref-tags">${items.map((item) => `<span class="pill pill-category">${escapeHtml(item)}</span>`).join("")}</div>`;
}

export function formatTime(queuedAt: string, completedAt: string | null): string {
	const queued = new Date(queuedAt);
	const queuedStr = queued.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
	if (completedAt) {
		const completed = new Date(completedAt);
		const diffMs = completed.getTime() - queued.getTime();
		const diffMin = Math.round(diffMs / 60000);
		if (diffMin < 60) return `Submitted ${queuedStr} · Completed in ${diffMin}m`;
		const diffHr = Math.round(diffMin / 60);
		return `Submitted ${queuedStr} · Completed in ${diffHr}h`;
	}
	return `Submitted ${queuedStr}`;
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes}B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

function escapeHtml(s: string): string {
	return s.replace(/[&<>"']/g, (c) =>
		({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c),
	);
}

function escapeAttr(s: string): string {
	return s.replace(/[&<>"']/g, (c) =>
		({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] ?? c),
	);
}
